import {
  Color,
  Dropdown,
  Icon,
  Image,
  Number,
  RichText,
  Screen,
  Screens,
  Section,
  Slider,
  Switch,
  Textarea,
  Textfield,
  OptionEditor,
  MultiField,
  Radio,
  Products,
} from '../formFields'
const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
]

const FORM_ID_TOOLTIP =
  'If Webhook URL is provided, setting Form Id will modify request payload to only include fields with matching Form Id. Otherwise everything is sent. '
const NAME_TOOLTIP =
  'Name of the property under which field value will be stored in request or dataLayer. Overriden by path. Either name or path must be set to store field in dataLayer or request payload.'
const PATH_TOOLTIP =
  'Path under which field value will be stored in request or dataLayer. Must match AEP schema to enable pre-filling from profile. Eg. person.name.firstName. If empty, name is used. . Either name or path must be set to store field in dataLayer or request payload.'
const PRE_FILL_TOOLTIP =
  "If empty, try to populate this field with data inserted in current browser tab or from AEP Profile. name or path must match other component's or AEP schema."
const DEFAULT_VALUE_TOOLTIP =
  'Fixed value used for pre-filling the empty field if no matching data is found in local and AEP Profile'
const name = Textfield({
  name: 'name',
  label: 'Field Name',
  tooltip: NAME_TOOLTIP,
})
const path = Textfield({
  name: 'path',
  label: 'Data storage path',
  tooltip: PATH_TOOLTIP,
})
const isRequired = Switch({
  name: 'isRequired',
  label: 'Is required',
  defaultChecked: false,
  tooltip: 'Visually mark field as required.',
})
const preFill = Switch({
  name: 'preFill',
  label: 'Pre-fill if possible',
  defaultChecked: true,
  tooltip: PRE_FILL_TOOLTIP,
})
const defaultValue = Textfield({
  name: 'defaultValue',
  label: 'Fixed default value',
  tooltip: DEFAULT_VALUE_TOOLTIP,
})
const formId = Textfield({
  name: 'formId',
  label: 'Form Id',
  tooltip: FORM_ID_TOOLTIP,
})

const alignment = Dropdown({
  name: 'alignment',
  label: 'Alignment',
  options: ALIGNMENT_OPTIONS,
  filter: null,
})

const eventType = Dropdown({
  name: 'events',
  label: 'Event Type',
  options: [
    { label: 'Product View', value: 'commerce.productViews' },
    { label: 'Product Add', value: 'commerce.productListAdds' },
    { label: 'Checkout', value: 'commerce.checkouts' },
    { label: 'Custom Event', value: 'customEvent' },
    { label: ' --- ', value: '' },
  ],
  filter: null,
})

const customEvent = Textfield({
  name: 'customEvent',
  label: 'Custom Event',
  hidden: (component) => component?.content?.events !== 'customEvent',
})

const listProps = [
  Dropdown({
    name: 'layout',
    label: 'Layout',
    options: [
      { label: 'Vertical/Grid', value: 'vertical/grid' },
      { label: 'Horizontal', value: 'horizontal' },
    ],
    filter: null,
  }),
  Slider({
    name: 'itemsInRow',
    label: 'Items in row',
    min: 0,
    max: 12,
    hidden: (component) => component?.content?.layout !== 'vertical/grid',
  }),
  Number({
    name: 'imageWidth',
    label: 'Single Item Width (px)',
    min: 0,
    max: 1000,
    hidden: (component) => component?.content?.layout !== 'horizontal',
  }),
  Number({
    name: 'itemHeight',
    label: 'Single Item Height (px)',
    min: 0,
    max: 1000,
    hidden: (component) => component?.content?.layout !== 'horizontal',
  }),
  // In this dropdown, *layouts* array (if present) is used to white-list renderer options based on component *layout*
  Dropdown({
    name: 'itemRenderer',
    label: 'Single List Item Renderer',
    options: [
      { label: 'Image and Text in one line', value: 'category-item-renderer', layouts: ['vertical/grid'] },
      { label: 'Image, Text, CTA in one line', value: 'category-with-cta-item-renderer', layouts: ['vertical/grid'] },
      { label: 'Image and Text overlay', value: 'banner-overlay', layouts: ['horizontal', 'vertical/grid'] },
      { label: 'Image and Text below', value: 'banner-stacked', layouts: ['horizontal', 'vertical/grid'] },
      { label: 'FSI Transactions', value: 'transaction-item-renderer', layouts: ['vertical/grid'] },
      { label: 'Single Image', value: 'single-image', layouts: ['horizontal', 'vertical/grid'] },
    ],
    filter: (opt, cmp) => (opt.layouts ? opt.layouts.includes(cmp.content.layout) : true),
  }),
  Dropdown({
    name: 'imageResizeMode',
    label: 'Single Image Resize',
    options: [
      { label: 'Contain', value: 'contain' },
      {
        label: 'Cover',
        value: 'cover',
      },
      { label: 'Stretch', value: 'stretch' },
    ],
    filter: null,
  }),
  Textfield({ name: 'buttonLabel', label: 'Single Item Button Label' }),
  Textarea({ name: 'data', label: 'JSON content - use only for FSI Transactions variant' }),
]
export const banner = {
  name: 'banner',
  label: 'Banner',
  description: 'Displays an image with optional title, subtitle and button. Can link to other screen or product.',
  hasChildren: false,
  fields: [
    Section(
      'Image',
      Image({ name: 'image', label: 'Image' }),
      Dropdown({
        name: 'size',
        label: 'Size',
        options: [
          { label: 'Small', value: 'small' },
          { label: 'Medium', value: 'medium' },
          { label: 'Large', value: 'large' },
          { label: 'Extra Large', value: 'xlarge' },
        ],
        filter: null,
      }),
      Dropdown({
        name: 'horizontalAlignment',
        label: 'Horizontal Alignment',
        options: [
          { label: 'Left', value: 'left' },
          { label: 'Center', value: 'center' },
          { label: 'Right', value: 'right' },
        ],
        filter: null,
      })
    ),

    Section(
      'Text',
      Textfield({ name: 'title', label: 'Title' }),
      Textfield({ name: 'subtitle', label: 'Subtitle' }),
      Textfield({ name: 'buttonLabel', label: 'Button Label (optional)' }),
      Screen({ name: 'linkTo', label: 'Link to', filter: null, allowProduct: true })
    ),

    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Overlay', value: 'full-overlay' },
        { label: 'Partial Overlay', value: 'partial-overlay' },
        { label: 'Stacked', value: 'stacked' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'banner' },
    content: {
      title: '',
      subtitle: '',
      buttonLabel: '',
      linkTo: '',
      image: null,
      layout: 'full-overlay',
      horizontalAlignment: 'center',
    },
  },
}

export const bannerV3 = {
  name: 'banner V3',
  label: 'Banner V3',
  description: 'Displays an image with optional title, subtitle and button. Can link to other screen or product.',

  fields: [
    Image({ name: 'image', label: 'Image' }),
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
      ],
      filter: null,
    }),

    Screen({ name: 'linkTo', label: 'Link to', filter: null, allowProduct: true }),
    Section(
      'Text',
      Textfield({ name: 'title', label: 'Title' }),
      Textfield({ name: 'subtitle', label: 'Subtitle' }),
      Textfield({ name: 'buttonLabel', label: 'Button Label (optional)' })
    ),
    Section(
      'Styles',
      Number({
        name: 'imgHeight',
        label: 'Custom image height',
        min: 0,
        max: 500,
      }),
      Number({
        name: 'imgWidth',
        label: 'Custom image width',
        min: 0,
        max: 500,
      }),
      Number({
        name: 'fontSize',
        label: 'Font Size',
        min: 0,
        max: 50,
      }),

      Number({
        name: 'padding',
        label: 'Padding',
        min: 0,
        max: 50,
      }),
      Color({ name: 'textColor', label: 'Text Color' }),
      Dropdown({
        name: 'textAlign',
        label: 'Text Align',
        options: [
          { label: 'Top Right', value: 'topRight' },
          { label: 'Top', value: 'top' },
          { label: 'Top Left', value: 'topLeft' },
          { label: 'Center Left', value: 'centerLeft' },
          { label: 'Center', value: 'center' },
          { label: 'Center Right', value: 'centerRight' },
          { label: 'Bottom Left', value: 'bottomLeft' },
          { label: 'Bottom', value: 'bottom' },
          { label: 'Bottom Right', value: 'BottomRight' },
        ],
        filter: null,
      })
    ),
  ],
  defaultContent: {
    meta: { component: 'bannerV3' },
    content: {
      title: '',
      subtitle: '',
      buttonLabel: '',
      linkTo: '',
      image: null,
    },
  },
}

export const card = {
  name: 'card',
  label: 'Card',
  hasChildren: false,
  fields: [
    Radio({
      name: 'type',
      label: '',
      options: [
        { label: 'Icon', value: 'icon' },
        { label: 'Image', value: 'image' },
      ],
      defaultValue: 'icon',
    }),
    Icon({ name: 'icon', label: 'Icon', hidden: (cmp) => cmp.content.type === 'image' }),
    Number({
      name: 'iconSize',
      label: 'IconSize',
      hidden: (cmp) => cmp.content.type === 'image',
      min: 0,
      max: 999,
      step: 1,
    }),
    Image({ name: 'image', label: 'Image', hidden: (cmp) => cmp.content.type !== 'image' }),
    Textfield({ name: 'title', label: 'Title' }),
    Textfield({ name: 'subtitle', label: 'Subtitle' }),
    Screen({ name: 'link', label: 'Link to', filter: null, allowExternal: false, allowProduct: true }),
  ],
  defaultContent: {
    meta: { component: 'card' },
    content: { type: 'icon', icon: 'globe', iconSize: 32 },
  },
}

export const userLogin = {
  name: 'userLogin',
  label: 'User Login',
  description: 'Displays user login form and on submit takes to profile screen.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'userLabel', label: 'User Label' }),
    Switch({ name: 'hidePasswordField', label: 'Hide Password Field', defaultChecked: false }),
    Textfield({ name: 'passwordLabel', label: 'Password Label' }),
    Textfield({ name: 'loginButtonLabel', label: 'Button Label (optional)' }),
    Textfield({ name: 'loginTitle', label: 'Login Title' }),
    Dropdown({
      name: 'loginButtonVariant',
      label: 'Login Button Variant',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'CTA', value: 'cta' },
        { label: 'Warning', value: 'warning' },
        { label: 'Quiet', value: 'quiet' },
        { label: 'Secondary', value: 'secondary' },
      ],
      filter: null,
    }),
    Screen({ name: 'link', label: 'Navigate to', filter: null }),
    Color({ name: 'textColor', label: 'Text Color' }),
  ],
  defaultContent: {
    meta: { component: 'userLogin' },
    content: {
      userLabel: '',
      passwordLabel: '',
      loginButtonLabel: '',
      loginTitle: '',
      loginButtonVariant: 'cta',
      hidePasswordField: false,
    },
  },
}

export const button = {
  name: 'button',
  label: 'Button',
  description: 'Displays a button with specified label and visual variant. Can link to screen or product.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Icon({ name: 'icon', label: 'Select icon' }),
    Dropdown({
      name: 'variant',
      label: 'Variant',
      options: [
        { label: 'Primary', value: 'primary' },
        { label: 'CTA', value: 'cta' },
        { label: 'Warning', value: 'warning' },
        { label: 'Quiet', value: 'quiet' },
        { label: 'Secondary', value: 'secondary' },
      ],
      filter: null,
    }),
    Screen({ name: 'link', label: 'Navigate to', filter: null, allowProduct: true }),
    eventType,
    customEvent,
    Section('Alignment', alignment),
    Dropdown({
      name: 'action',
      label: 'Action',
      options: [
        { label: 'Reset Profile', value: 'resetProfile' },
        { label: 'Resolve AEP Segment', value: 'resolveSegment' },
        { label: ' --- ', value: '' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'resolveSegment',
      label: 'On-click Segment Resolve',
      options: [
        { label: "Luma - Women's Category Interest", value: 'womenCategoryInterest' },
        { label: "Luma - Men's Category Interest", value: 'menCategoryInterest' },
        { label: ' --- ', value: '' },
      ],
      filter: null,
      hidden: (component) => component?.content?.action !== 'resolveSegment',
    }),
    Switch({ name: 'emptyCartOption', label: 'Empty the Cart', defaultChecked: false }),
    OptionEditor({ name: 'customStyles', label: 'Custom Styles' }),
    Textfield({ name: 'message', label: 'Message' }),
    Color({ name: 'buttonTextColor', label: 'Text Color' }),
  ],
  defaultContent: {
    meta: { component: 'button' },
    content: {
      label: 'Click',
      variant: 'primary',
      screen: '',
      events: ' --- ',
      customEvent: 'custom',
      message: '',
      action: ' --- ',
      resolveSegment: ' --- ',
      emptyCart: false,
    },
  },
}

export const divider = {
  name: 'divider',
  label: 'Divider',
  description: 'Displays a visual divider, mostly horizontal.',
  hasChildren: false,

  fields: [
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'S', value: 'S' },
        { label: 'M', value: 'M' },
        { label: 'L', value: 'L' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'orientation',
      label: 'Orientation',
      options: [
        { label: 'Horizontal', value: 'horizontal' },
        { label: 'Vertical', value: 'vertical' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'length',
      label: 'Length',
      options: [
        { label: 'S', value: 'S' },
        { label: 'M', value: 'M' },
        { label: 'L', value: 'L' },
        { label: 'XL', value: 'XL' },
      ],
      filter: null,
    }),
    Switch({ name: 'showLine', label: 'Show line', defaultChecked: true }),
    Color({ name: 'dividerColor', label: 'Divider Color' }),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    meta: { component: 'divider' },
    content: {
      size: 'L',
      orientation: 'horizontal',
      length: 'M',
      alignment: 'center',
    },
  },
}

export const htmlText = {
  name: 'htmlText',
  label: 'Rich Text',
  description: 'Displays an arbitrary HTML. Use with caution!',
  hasChildren: false,
  fields: [RichText({ name: 'text', label: 'Content' })],
  defaultContent: {
    meta: { component: 'htmlText' },
    content: {
      text: 'Placeholder for a Rich Text',
    },
  },
}

export const search = {
  name: 'search',
  label: 'Search',
  description: 'Search component with text field and button',
  hasChildren: false,
  fields: [
    Textfield({ name: 'name', label: 'Name' }),
    Textfield({ name: 'label', label: 'Label' }),
    Textfield({ name: 'path', label: 'Path' }),
    Textfield({ name: 'placeholder', label: 'Placeholder' }),
    Icon({ name: 'icon', label: 'Select icon' }),
    Screen({ name: 'link', label: 'Navigate to', filter: null, allowProduct: true }),
    eventType,
    customEvent,
  ],
  defaultContent: {
    meta: { component: 'search' },
    content: {
      text: 'Placeholder for a Rich Text',
    },
  },
}

export const personalisationDebug = {
  name: 'personalisationDebug',
  label: 'Personalisation Debug',
  category: 'Technical Project',
  description: 'Display details about the qualified activities and experiences from the Target',
  hasChildren: false,
  fields: [Textfield({ name: 'mboxName', label: 'Target Mbox Name' })],
  defaultContent: {
    meta: { component: 'personalisationDebug' },
    content: {},
  },
}
export const beaconsSimulation = {
  name: 'beaconsSimulation',
  label: 'Display Beacons',
  category: 'Technical Project',
  description: 'Display List of beacons and simulate interation with AEP',
  hasChildren: false,
  fields: [
    Switch({
      name: 'liveBeacon',
      label: 'Live Beacon',
      defaultChecked: false,
      tooltip:
        'Display a list of live/dummy beacons. Live beacons are displayed from the list entered in the trigger section',
    }),
    OptionEditor({
      name: 'beaconsList',
      label: 'Simuate Beacons Events',
      columns: [
        { label: 'Title', name: 'title' },
        {
          label: 'EventId',
          name: 'eventId',
          tooltip: "Provide Journey's eventId if you want to send push/in-app on entry or exit of the beacon.",
        },
        //{ label: 'Event Type', name: 'eventType' },
        { label: 'Location', name: 'location' },
        { label: 'Icon', name: 'icon', type: 'icon' },
      ],
      hidden: (component) => component?.content?.liveBeacon !== false,
    }),
  ],
  defaultContent: {
    meta: { component: 'beaconsSimulation' },
    content: {
      liveBeacon: false,
    },
  },
}

export const map = {
  name: 'map',
  label: 'Map',
  fields: [
    OptionEditor({
      name: 'mapCoordinates',
      label: 'Coordinates',
      columns: [
        { label: 'Label', name: 'label' },
        { label: 'Latitude', name: 'latitude' },
        { label: 'Longitude', name: 'longitude' },
        { label: 'Screen Open OnClick', name: 'link' },
      ],
    }),
    Textfield({ name: 'width', label: 'Width' }),
    Textfield({ name: 'height', label: 'Height' }),
  ],
  defaultContent: {
    meta: { component: 'map' },
    content: {
      width: '100%',
      height: '100%',
    },
  },
}

export const iconTextRenderer = {
  name: 'iconTextRenderer',
  label: 'Icon + Text',
  description:
    'Displays an icon followed by text. Allows to select Font Awesome icon, provide text content and choose color.',
  hasChildren: false,
  fields: [
    Icon({ name: 'icon', label: 'Select icon' }),
    Textfield({ name: 'text', label: 'Text' }),
    Color({ name: 'color', label: 'Color' }),
  ],
  defaultContent: {
    meta: { component: 'iconTextRenderer' },
    content: {},
  },
}

export const iconImageTextSubtitle = {
  name: 'iconImageTextSubtitle',
  label: 'Icon/Image + Text + Subtitle',
  hasChildren: false,
  fields: [
    Icon({ name: 'text', label: 'Select icon' }),
    Image({ name: 'src', label: 'Image' }),
    Textfield({ name: 'title', label: 'label' }),
    Textfield({ name: 'subtitle', label: 'label' }),
  ],
  defaultContent: {
    meta: { component: 'iconImageTextSubtitle' },
    content: {},
  },
}

export const video = {
  name: 'video',
  label: 'Video',
  hasChildren: false,
  fields: [
    Textfield({ name: 'url', label: 'Video URL' }),
    Textfield({ name: 'videoPoster', label: 'Video Poster' }),
    Switch({ name: 'autoplay', label: 'Autoplay', defaultChecked: true }),
  ],
  defaultContent: {
    meta: { component: 'video' },
    content: {
      autoplay: true,
    },
  },
}

export const image = {
  name: 'image',
  label: 'Image',
  hasChildren: false,
  fields: [
    Image({ name: 'src', label: 'Image' }),
    Switch({ name: 'showBackground', label: 'Full Width', defaultChecked: true }),

    Dropdown({
      name: 'imageSize',
      label: 'Size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
        { label: 'XLarge', value: 'xlarge' },
        { label: 'fullWidth', value: 'fullWidth' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'imageResize',
      label: 'Image Resize',
      options: [
        { label: 'none', value: 'none' },
        { label: 'contain', value: 'contain' },
        {
          label: 'cover',
          value: 'cover',
        },
        { label: 'stretch', value: 'stretch' },
        { label: 'repeat', value: 'repeat' },
        { label: 'center', value: 'center' },
      ],
      filter: null,
    }),
    Number({
      name: 'height',
      label: 'Custom image height',
      min: 0,
      max: 1000,
    }),
    Number({
      name: 'width',
      label: 'Custom image width',
      min: 0,
      max: 100,
    }),
    Number({
      name: 'border',
      label: 'Custom border radius',
      min: 0,
      max: 100,
    }),
    Screen({ name: 'linkTo', label: 'Link to', filter: null }),
    Section('Email', Switch({ name: 'emailEnabled', label: 'Enabled', defaultChecked: true })),
  ],
  defaultContent: {
    meta: { component: 'image' },
    content: {
      src: null,
      showBackground: false,
      imageSize: 'medium',
      imageResize: 'cover',
      linkTo: '',
    },
  },
}

export const list = {
  name: 'list',
  label: 'List',
  hasChildren: false,
  fields: [Screens({ name: 'items', label: 'Screens', filter: null }), ...listProps],
  defaultContent: {
    meta: { component: 'list' },
    content: {
      layout: 'horizontal',
      itemRenderer: 'banner-overlay',
      itemsInRow: 0,
      items: [],
      imageWidth: 120,
      imageResizeMode: 'cover',
    },
  },
}

export const productList = {
  name: 'productList',
  label: 'Product List',
  category: 'Product',
  hasChildren: false,
  fields: [
    Products({ name: 'products', label: 'Products', filter: null }),
    Screen({ name: 'productScreen', label: 'Product Screen (Template)', filter: null }),
    ...listProps,
  ],
  defaultContent: {
    meta: { component: 'productList' },
    content: {
      layout: 'horizontal',
      itemRenderer: 'banner-overlay',
      itemsInRow: 0,
      products: [],
      imageWidth: 120,
      imageResizeMode: 'cover',
    },
  },
}

export const progressBar = {
  name: 'progressBar',
  label: 'Progress Bar',
  description: 'Displays a progress bar (static or dynamic) with several visual options.',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'type',
      label: 'Type',
      options: [
        { label: 'Dynamic', value: 'dynamic' },
        { label: 'Static', value: 'static' },
      ],
      filter: null,
    }),
    Number({
      name: 'value',
      label: 'Value (0.0 - 1.0)',
      min: 0.0,
      max: 1.0,
      step: 0.1,
      hidden: (cmp) => cmp.content.type !== 'static',
    }),
    Textfield({ name: 'text', label: 'Text' }),
    Textfield({ name: 'finalText', label: 'Final text' }),
    Icon({ name: 'icon', label: 'Icon (optional)' }),
    Dropdown({
      name: 'justifyContent',
      label: 'Justify Content',
      options: [
        { label: 'Center', value: 'center' },
        { label: 'Left', value: 'flex-start' },
        { label: 'Right', value: 'flex-end' },
      ],
      filter: null,
    }),
    Number({
      name: 'width',
      label: 'Width (px)',
      min: 50,
      max: 600,
    }),
  ],
  defaultContent: {
    meta: { component: 'progressBar' },
    content: { text: 'loading', type: 'dynamic' },
  },
}

export const panel = {
  name: 'panel',
  label: 'Panel',
  description:
    'Serves as a container for other components. Has several options to visually highlight a group of components, eg. by additional padding',
  hasChildren: true,
  fields: [
    Textfield({ name: 'panelTitle', label: 'Title' }),
    Number({ name: 'panelBorderRadius', label: 'Border Radius (px)', min: 0, max: 500 }),
    Number({ name: 'panelPadding', label: 'Padding (px)', min: 0, max: 500 }),
    Number({ name: 'panelMargin', label: 'Margin (px)', min: 0, max: 500 }),
    Color({ name: 'panelBackgroundColor', label: 'Background Color' }),
    Textfield({ name: 'panelBackgroundImage', label: 'Background Image' }),
    Switch({ name: 'showBorder', label: 'Show Border', defaultChecked: true }),
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical' },
        { label: 'Horizontal', value: 'horizontal' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'itemsAlignment',
      label: 'Content Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      filter: null,
    }),
    Textfield({ name: 'panelHeight', label: 'Panel height (px)' }),
    Switch({ name: 'panelFullHeight', label: 'Panel Height (100%)', defaultChecked: false }),
  ],
  defaultContent: {
    meta: {
      component: 'panel',
      showBorder: true,
      layout: 'vertical',
    },
    content: [],
  },
}

export const title = {
  name: 'title',
  label: 'Title',
  description: 'Displays a title, giving control over text, size and font weight.',
  hasChildren: false,
  fields: [
    Textfield({ name: 'text', label: 'Text' }),
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'X Small', value: 'xsmall' },
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
        { label: 'X Large', value: 'xlarge' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'weight',
      label: 'Weight',
      options: [
        { label: 'Light', value: 'light' },
        { label: 'Regular', value: 'regular' },
        { label: 'Bold', value: 'bold' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'textAlignment',
      label: 'Text Alignment',
      options: [
        { label: 'Left', value: 'left' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'right' },
      ],
      filter: null,
    }),
    Color({ name: 'textColor', label: 'Text Color' }),
  ],
  defaultContent: {
    meta: { component: 'title' },
    content: { text: 'Title', size: 'medium', weight: 'regular', textAlignment: 'left' },
  },
}

export const offerRendererV2 = {
  name: 'offerRendererV2',
  label: 'Offer',
  description: 'Displays an offer from Code Based Experience',
  hasChildren: false,
  fields: [
    Textfield({ name: 'surface', label: 'Surface' }),
    Textfield({ name: 'fallbackImage', label: 'Fallback Image' }),
    Dropdown({
      name: 'size',
      label: 'Size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'imageResize',
      label: 'Image Resize',
      options: [
        { label: 'none', value: 'none' },
        { label: 'contain', value: 'contain' },
        {
          label: 'cover',
          value: 'cover',
        },
        { label: 'stretch', value: 'stretch' },
        { label: 'repeat', value: 'repeat' },
        { label: 'center', value: 'center' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'offerRendererV2' },
    content: {
      text: 'OfferV2',
    },
  },
}

export const contentCards = {
  name: 'contentCards',
  label: 'Content Cards',
  description: 'Display a Content Card',
  hasChildren: false,
  fields: [Textfield({ name: 'surface', label: 'Surface' })],
  defaultContent: {
    meta: { component: 'contentCards' },
    content: {
      text: 'contentCards',
    },
  },
}

export const webView = {
  name: 'webView',
  label: 'Web View',
  description: 'Open URL in web view',
  hasChildren: false,
  fields: [Textfield({ name: 'url', label: 'URL' })],
  defaultContent: {
    meta: { component: 'webView' },
    content: {
      url: 'https://www.adobe.com',
    },
  },
}

// Product Sub-Components

export const productAddToCartButton = {
  name: 'productAddToCartButton',
  label: 'Add To Cart Button',
  category: 'Product',
  fields: [
    Textfield({ name: 'buttonLabel', label: 'Button Label' }),
    Textarea({ name: 'message', label: 'Confirmation Message' }),
  ],
  defaultContent: {
    meta: { component: 'productAddToCartButton' },
    content: { product: null, buttonLabel: 'Add to cart', message: 'Product added to cart.' },
  },
}

export const productDescription = {
  name: 'productDescription',
  label: 'Product Description',
  category: 'Product',
  fields: [Color({ name: 'color', label: 'Color' })],
  defaultContent: {
    meta: { component: 'productDescription' },
    content: { product: null },
  },
}

export const productVideo = {
  name: 'productVideo',
  label: 'Product Video',
  category: 'Product',
  fields: [Textfield({ name: 'videoPoster', label: 'Video Poster' })],
  defaultContent: {
    meta: { component: 'productVideo' },
    content: { product: null },
  },
}

export const productImage = {
  name: 'productImage',
  label: 'Product Image',
  category: 'Product',
  fields: [
    Dropdown({
      name: 'resizeMode',
      label: 'Resize Mode',
      options: [
        { label: 'Contain', value: 'contain' },
        {
          label: 'Cover',
          value: 'cover',
        },
        { label: 'Stretch', value: 'stretch' },
      ],
      filter: null,
    }),
  ],

  defaultContent: {
    meta: { component: 'productImage' },
    content: { product: null },
  },
}

export const productTitle = {
  name: 'productTitle',
  label: 'Product Title',
  category: 'Product',
  fields: [Color({ name: 'color', label: 'Color' })],
  defaultContent: {
    meta: { component: 'productTitle' },
    content: { product: null },
  },
}

export const fsiProductSelectorWithDetails = {
  name: 'fsiProductSelectorWithDetails',
  label: 'FSI Product Selector with Details',
  category: 'Product',
  fields: [
    Screens({ name: 'options', label: 'Options (Select Product Screens)', filter: null }),
    Textfield({ name: 'label', label: 'Selection Title' }),
    Textfield({ name: 'formId', label: 'FormId' }),
    Dropdown({
      name: 'variant',
      label: 'Variant',
      options: [
        { label: 'Basic', value: 'basic' },
        { label: 'Advance', value: 'Advance' },
      ],
      filter: null,
    }),

    Dropdown({
      name: 'imageSize',
      label: 'Size',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Medium', value: 'medium' },
        { label: 'Large', value: 'large' },
        { label: 'XLarge', value: 'xlarge' },
        { label: 'fullWidth', value: 'fullWidth' },
      ],
      filter: null,
      hidden: (component) => component?.content?.variant !== 'basic',
    }),
    Dropdown({
      name: 'imageResize',
      label: 'Image Resize',
      options: [
        { label: 'Scale to show full image', value: 'contain' },
        {
          label: 'Scale to cover container size',
          value: 'cover',
        },
        { label: 'Stretch to cover container size', value: 'stretch' },
      ],
      filter: null,
      hidden: (component) => component?.content?.variant !== 'basic',
    }),
  ],
  defaultContent: {
    meta: { component: 'fsiProductSelectorWithDetails' },
    content: {
      options: [],
      label: 'SELECT',
      formId: '',
      variant: 'Advance',
      showBackground: false,
      imageSize: 'medium',
      imageResize: 'cover',
    },
  },
}

export const fsiProductSummary = {
  name: 'fsiProductSummary',
  label: 'FSI Product Summary',
  category: 'Product',
  fields: [],
  defaultContent: {
    meta: { component: 'fsiProductSummary' },
    content: {},
  },
}

export const checkbox = {
  name: 'checkbox',
  label: 'Checkbox',
  category: 'Form',
  description: 'Displays ',
  fields: [
    OptionEditor({ name: 'options', label: 'Options' }),
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    meta: { component: 'checkbox' },
    content: { alignment: 'left', preFill: true },
  },
}

export const radioButton = {
  name: 'radioButton',
  label: 'Radio Button',
  description: 'Displays ',
  category: 'Form',
  fields: [
    OptionEditor({ name: 'options', label: 'Options' }),
    isRequired,
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
    Switch({ name: 'horizontalLayout', label: 'Horizontal Layout', defaultChecked: true }),
  ],
  defaultContent: {
    meta: { component: 'radioButton' },
    content: { alignment: 'left', preFill: true },
  },
}

export const dropdown = {
  name: 'dropdown',
  label: 'Dropdown',
  description: 'Displays one ore more options in drop-down picker.',
  category: 'Form',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    OptionEditor({
      name: 'options',
      label: 'Options',
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'value', label: 'Value' },
      ],
    }),
    Section('Data Handling', name, path, formId, defaultValue, preFill),
    Section('Alignment', alignment),
  ],
  defaultContent: {
    meta: { component: 'dropdown' },
    content: { alignment: 'left', preFill: true },
  },
}

export const textField = {
  name: 'textField',
  label: 'Text Field',
  category: 'Form',
  fields: [
    Textfield({ name: 'name', label: 'Name' }),
    Textfield({ name: 'label', label: 'Label' }),
    Textfield({ name: 'defaultValue', label: 'Default Value' }),
    Textfield({ name: 'path', label: 'Path' }),
    Switch({ name: 'preFill', label: 'preFill', defaultChecked: true }),
    Switch({ name: 'hidden', label: 'Hide the field', defaultChecked: false }),
    Textfield({ name: 'formId', label: 'FormId' }),
    Textfield({ name: 'placeholder', label: 'Placeholder' }),
    Color({ name: 'textColor', label: 'Text Color' }),
    Textfield({ name: 'width', label: 'Width' }),
  ],
  defaultContent: {
    meta: { component: 'textField' },
    content: { label: 'NAME', placeholder: 'PROVIDE YOUR NAME', name: 'name', path: '', formId: '', preFill: true },
  },
}

export const formSubmitButton = {
  name: 'formSubmitButton',
  label: 'Submit Button',
  category: 'Form',
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    eventType,
    customEvent,
    Textfield({ name: 'webhookUrl', label: 'WebhookUrl' }),
    Textfield({ name: 'formId', label: 'FormId' }),
    Textfield({ name: 'message', label: 'Message' }),
    Screen({ name: 'link', label: 'Navigate to', filter: null }),
    Switch({ name: 'emptyCartOption', label: 'Empty the Cart', defaultChecked: false }),
    Dropdown({
      name: 'alignItems',
      label: 'Align',
      options: [
        { label: 'Left', value: 'flex-start' },
        { label: 'Center', value: 'center' },
        { label: 'Right', value: 'flex-end' },
      ],

      filter: null,
    }),
  ],

  defaultContent: {
    meta: { component: 'formSubmitButton' },
    content: { label: 'SUBMIT', message: 'Thank you' },
    events: ' --- ',
    webhookUrl: '',
    formId: '',
    customEvent: 'custom',
  },
}

export const timepicker = {
  name: 'timepicker',
  label: 'Time Slot Picker',
  category: 'Form',
  fields: [
    Switch({ name: 'multiple', label: 'Select multiple', defaultChecked: false }),

    Section(
      'Options',
      Number({ name: 'days', label: 'Displayed days', min: 1, max: 7 }),
      MultiField({ name: 'dailyOptions', label: 'Daily Options' })
    ),
    Section('Data Handling', name, path, formId),
    eventType,
    customEvent,
    Textfield({ name: 'webhookUrl', label: 'WebhookUrl' }),
    Section('Alignment', alignment),
  ],

  defaultContent: {
    meta: { component: 'timepicker' },
    content: {
      multiple: false,
      days: 3,
      dailyOptions: ['9 AM', '10 AM', '11 AM', '12 AM'],
      events: ' --- ',
      webhookUrl: '',
    },
  },
}

export const reviewOrderButton = {
  name: 'reviewOrderButton',
  label: 'Review Order',
  description: 'Displays a button with specified label and visual variant. Can link to screen or product.',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'buttonLabel', label: 'Button Label' }),
    Screen({ name: 'linkTo', label: 'Link to', filter: null }),
    eventType,
    customEvent,
    Textfield({ name: 'message', label: 'Message' }),
  ],
  defaultContent: {
    meta: { component: 'reviewOrderButton' },
    content: {
      buttonLabel: 'REVIEW ORDER',
      linkTo: 'reviewOrder',
    },
  },
}

export const cartItems = {
  name: 'cartItems',
  label: 'Cart Items',
  description: 'Displays list of products added in the cart',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'emptyCartButtonLabel', label: 'Empty Cart Button Label' }),
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical-list' },
        { label: 'Horizontal', value: 'horizontal-list' },
      ],

      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'cartItems' },
    content: {
      layout: 'vertical-list',
    },
  },
}

export const cartSummary = {
  name: 'cartSummary',
  label: 'Cart Summary',
  description: 'Displays list of products added in the cart',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'label', label: 'Label' }),
    Dropdown({
      name: 'showDiscount',
      label: 'Show Discount',
      options: [
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
      filter: null,
    }),
    Dropdown({
      name: 'showPromoCode',
      label: 'show Promo Code',
      options: [
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
      filter: null,
    }),
    Textfield({ name: 'promoCodeLabel', label: 'Promo Code Label' }),
    Dropdown({
      name: 'showShippingCharge',
      label: 'Show Shipping Charge',
      options: [
        { label: 'True', value: 'true' },
        { label: 'False', value: 'false' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'cartSummary' },
    content: {
      label: 'TOTAL',
      showDiscount: 'true',
      showPromoCode: 'false',
      promoCodeLabel: 'PROMO CODE',
      showShippingCharge: 'true',
    },
  },
}

export const cartButtons = {
  name: 'cartButtons',
  label: 'Cart Button',
  description: 'Displays Button on cart screen',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'emptyButtonLabel', label: 'Empty Button Label' }),
    Textfield({ name: 'checkoutButtonLabel', label: 'Checkout Button Label' }),
    eventType,
    customEvent,
    Screen({ name: 'link', label: 'Navigate to', filter: null }),
    Textfield({ name: 'message', label: 'Message' }),
    Dropdown({
      name: 'action',
      label: 'Action',
      options: [
        { label: 'Empty Cart', value: 'emptyCart' },
        { label: ' --- ', value: '' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'cartButtons' },
    content: {
      emptyButtonLabel: 'EMPTY CART',
      checkoutButtonLabel: 'CHECKOUT',
      events: 'commerce.checkouts',
      promoCodeLabel: 'PROMO CODE',
    },
  },
}

export const orderButtons = {
  name: 'orderButtons',
  label: 'Order Button',
  description: 'Displays a button with specified label for review screen.',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'buttonLabel', label: 'Button Label' }),
    eventType,
    customEvent,
    Screen({ name: 'link', label: 'Navigate to', filter: null }),
    Textfield({ name: 'message', label: 'Message' }),
  ],
  defaultContent: {
    meta: { component: 'orderButtons' },
    content: {
      buttonLabel: 'CONFIRM ORDER',
    },
  },
}

// User Details components

export const profilePicture = {
  name: 'profilePicture',
  label: 'Profile Picture',
  description: 'Displays profile picture',
  category: 'User Details',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical-list' },
        { label: 'Horizontal', value: 'horizontal-list' },
      ],

      filter: null,
    }),

    Textfield({ name: 'formId', label: 'FormId' }),
    Textfield({ name: 'path', label: 'Path' }),
    Dropdown({
      name: 'enabled',
      label: 'Enabled',
      options: [
        { label: 'true', value: 'true' },
        { label: 'false', value: 'false' },
      ],
      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'profilePicture' },
    content: {
      layout: 'vertical-list',
    },
  },
}
export const profileDetails = {
  name: 'profileDetails',
  label: 'Profile Details',
  description: 'Displays user details: first and last name, profile picture',
  category: 'User Details',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical-list' },
        { label: 'Horizontal', value: 'horizontal-list' },
      ],

      filter: null,
    }),
    Textfield({ name: 'profileInfo', label: 'Provide Profile Information' }),
  ],
  defaultContent: {
    meta: { component: 'profileInfo' },
    content: {
      layout: 'vertical-list',
    },
  },
}

export const loyaltyDetails = {
  name: 'loyaltyDetails',
  label: 'Loyalty Details',
  description: 'Displays Loyalty details in user detail screen',
  category: 'User Details',
  hasChildren: false,
  fields: [],
  defaultContent: {
    meta: { component: 'loyaltyDetails' },
    content: {},
  },
}

// Favorites / Wish List components

export const wishList = {
  name: 'wishList',
  label: 'Wish List',
  description: 'Displays list of products marked as wished',
  category: 'Favorites / Wish List',
  hasChildren: false,
  fields: [
    Textfield({ name: 'wishListTitle', label: 'wish List Title' }),
    Textfield({ name: 'emptyWishListLabel', label: 'Empty Wish List Label' }),
  ],
  defaultContent: {
    meta: { component: 'wishList' },
    content: {
      wishListTitle: 'YOUR WISH LIST',
      emptyWishListLabel: 'EMPTY WISH LIST',
    },
  },
}

export const videoAddToFavButton = {
  name: 'videoAddToFavButton',
  label: 'Video Add To Favorites Button',
  category: 'Favorites / Wish List',
  fields: [],
  defaultContent: {
    meta: { component: 'videoAddToFavButton' },
    content: { product: null },
  },
}

export const videoItems = {
  name: 'videoItems',
  label: 'Video Items',
  description: 'Displays list of Favorites Video added in the list',
  category: 'Favorites / Wish List',
  hasChildren: false,
  fields: [
    Dropdown({
      name: 'layout',
      label: 'Layout',
      options: [
        { label: 'Vertical', value: 'vertical-list' },
        { label: 'Horizontal', value: 'horizontal-list' },
      ],

      filter: null,
    }),
  ],
  defaultContent: {
    meta: { component: 'videoItems' },
    content: {
      layout: 'vertical-list',
    },
  },
}

export const flightSearch = {
  name: 'flightSearch',
  label: 'Flight Search',
  category: 'Search',
  description:
    'Displays a simple flight search component with *from* and *to* fields as well as date and additional options.',
  fields: [
    Screen({ name: 'link', label: 'Link', filter: null }),
    Dropdown({
      name: 'type',
      label: 'Input type',
      options: [
        { label: 'Picker (from products)', value: 'automaticPicker' },
        { label: 'Picker (user-defined)', value: 'picker' },
        { label: 'Text field', value: '' },
      ],
      filter: null,
    }),
    OptionEditor({
      name: 'locations',
      label: 'Locations',
      columns: [
        { name: 'label', label: 'Label' },
        { name: 'value', label: 'Value' },
      ],
      saveAs: 'array',
      hidden: (cmp) => cmp.content.type !== 'picker',
    }),
    Textfield({
      name: 'from',
      label: 'From (optional)',
      tooltip: 'Default if no other value is found in dataLayer and URL.',
    }),
    Textfield({
      name: 'to',
      label: 'To (optional)',
      tooltip: 'Default if no other value is found in dataLayer and URL.',
    }),
    OptionEditor({
      name: 'options',
      label: 'Additional Options',
      columns: [
        { label: 'Label', name: 'label', type: 'text' },
        { label: 'Value', name: 'value', type: 'text' },
      ],
    }),
    Section(
      'Data Handling',
      eventType,
      customEvent,
      Textfield({
        name: 'path',
        label: 'Options storage path',
        tooltip: PATH_TOOLTIP,
      })
    ),
    Section(
      'Labels',
      Textfield({ name: 'fromLabel', label: 'From Label' }),
      Textfield({ name: 'toLabel', label: 'To Label' }),
      Textfield({ name: 'buttonLabel', label: 'Button Label' })
    ),
  ],
  defaultContent: {
    meta: {
      component: 'flightSearch',
    },
    content: {
      type: 'automaticPicker',
      fromLabel: 'From',
      toLabel: 'To',
      dateLabel: 'Date',
      buttonLabel: 'Search',
    },
  },
}

export const flightResults = {
  name: 'flightResults',
  label: 'Flight Results',
  category: 'Search',
  description: 'Displays flights that match Flight Search component input.',
  fields: [
    Screen({ name: 'link', label: 'Link', filter: null }),
    Textfield({
      name: 'from',
      label: 'From (optional)',
      tooltip:
        'Leave blank to read this value from URL or search form. Setting it will cause the component to ignore other sources.',
    }),
    Textfield({
      name: 'to',
      label: 'To (optional)',
      tooltip:
        'Leave blank to read this value from URL or search form. Setting it will cause the component to ignore other sources.',
    }),
    eventType,
    customEvent,
    Section(
      'Labels',
      Textfield({ name: 'buttonLabel', label: 'Button Label' }),
      Textfield({ name: 'emptyOptionLabel', label: 'Unavailable option label' }),
      Textfield({ name: 'noResultsLabel', label: 'No result text' })
    ),
  ],
  defaultContent: {
    meta: {
      component: 'flightResults',
    },
    content: {
      buttonLabel: 'Select',
      emptyOptionLabel: 'Not available',
      noResultsLabel: "We're sorry. We didn't find any flights matching your criteria.",
    },
  },
}

export const userShippingAddress = {
  name: 'userShippingAddress',
  label: 'Shipping Address',
  description: 'Displays shipping address form.',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [Textfield({ name: 'title', label: 'Title' })],
  defaultContent: {
    meta: { component: 'userShippingAddress' },
    content: {
      title: 'Shipping Address',
    },
  },
}

export const shippingMethods = {
  name: 'shippingMethods',
  label: 'Shipping Methods',
  description: 'Displays shipping method selection.',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    OptionEditor({
      name: 'options',
      label: 'Shipping options',
    }),
  ],
  defaultContent: {
    meta: { component: 'shippingMethods' },
    content: {
      title: 'Shipping Method',
      options: [
        {
          label: 'Fast Delivery',
          value: '10',
        },
        {
          label: 'Standard Delivery',
          value: '5',
        },
      ],
    },
  },
}

export const paymentMethods = {
  name: 'paymentMethods',
  label: 'Payment Methods',
  description: 'Displays payment method selection.',
  category: 'Cart & Checkout',
  hasChildren: false,
  fields: [
    Textfield({ name: 'title', label: 'Title' }),
    OptionEditor({
      name: 'options',
      label: 'Payment options',
    }),
  ],
  defaultContent: {
    meta: { component: 'paymentMethods' },
    content: {
      title: 'Payment Method',
      options: [
        {
          label: 'CREDIT OR DEBIT CARD',
          value: '0',
        },
        {
          label: 'PAYPAL',
          value: '1',
        },
      ],
    },
  },
}

export const DEFAULT_COMPONENT = title.defaultContent
